/**
 * What the sky looks like, from the sun's height and the weather (T103 ② and ④).
 *
 * `sun.ts` says **where the sun is**. This says **what to do about it** — two
 * colours and three multipliers, from one number and one weather reading.
 *
 * ## Why elevation and nothing else decides the TIME of day
 *
 * Not the hour, not the date. The sky at 3° above the horizon looks the same
 * whether that is midwinter noon or a midsummer midnight — and in Iceland **both
 * of those happen**. Driving off elevation gets the long, slow Icelandic dusk
 * for free: in June the sun crawls along just under the horizon for hours, and
 * anything keyed to the clock instead would snap from day to night at a time.
 *
 * ## ✅ The gradient arrives here, and it is what piece ④ was waiting for
 *
 * §12.6 (V17) asks for **two stops, zenith and horizon**, and measured both from
 * haugur's photographs. Piece ② shipped **one flat colour** — the horizon stop —
 * which §12.6 explicitly calls defensible, because **overcast has no reliable
 * gradient at all**: three of five measured overcast frames are *darker* at the
 * horizon. **Clear skies measurably do have one — 1.74× zenith to horizon** —
 * so the gradient becomes real the moment clear weather can happen, and that is
 * now. **One mechanism serves both; the default simply collapses it.**
 *
 * 🔴 **Fog and the `HemisphereLight` still take the HORIZON stop, never the
 * zenith.** §12.6 gives the reason and it is not aesthetic: terrain must fade
 * into the colour the sky actually is *where the terrain meets it*, and a ground
 * surface integrates the whole upper hemisphere, most of which by solid angle is
 * near the horizon. Binding the light to the zenith — the most saturated part of
 * the sky — would tint every shadow strongly blue.
 *
 * ## ⚠️ Weather colours the DAY only
 *
 * The clear, overcast and fog stops are all **daylight** measurements. Dusk and
 * night are measured under their own conditions and are left alone, because
 * there is no photograph of a foggy Icelandic night in `Reference/` and mixing
 * a daylight fog grey into a night sky would produce a bright grey midnight.
 * **Fog still closes the view in at night** — that is `viewFactor`, which is not
 * a colour.
 */

/**
 * The **overcast** day, and the default (V17).
 *
 * **Measured**, from `Design/00_Visual_Language.md` §12.6 — four of haugur's ten
 * daylight photographs: `sky 9.png`, `sky2.jpg`, `sky 8.jpg`, `sky 7.jpg`.
 *
 * ⚠️ Overcast is the default deliberately: five of the nine daylight frames are
 * overcast, fog or storm. **The Icelandic sky is frequently not blue** —
 * `sky 8.jpg` samples at saturation 0.008, which is no colour at all.
 */
const DAY = '#a9b2ba';
const DAY_ZENITH = '#9ca9b3';

/**
 * The **clear** day. **Measured** — §12.6, from `sky4.jpg`, `sky 6.jpg`,
 * `sky3.jpg`.
 *
 * 🔴 **This is the pair that makes the gradient worth having**: 1.74× brighter at
 * the horizon than overhead, median of three frames. Nothing in the game has ever
 * shown it, because until the weather arrived there was no clear sky to show.
 */
const CLEAR = '#9cb9c8';
const CLEAR_ZENITH = '#5f92ac';

/**
 * **Fog.** **Measured** — §12.6, from `sky 5 foggy.jpg`. One stop, both ends.
 *
 * 🔴 **This colour has existed since 2026-08-07 with nothing to trigger it.** The
 * weather rows carry `fog` — SYNOP's code 9, *sky obscured* — and it was kept
 * through the data build for exactly this. §12.6: *"in fog the horizon genuinely
 * does not exist, and depicting that is not a cheat."*
 */
const FOG = '#a6aab0';

/**
 * Dusk. **Derived from a measurement, not itself a sampled stop** — and the
 * difference is worth stating.
 *
 * §12.6 records `dusk.jpg` as **hue 7–8°, luminance 0.022 → 0.049, saturation
 * 0.22 → 0.33** — a warm red-brown that is *brighter toward the horizon*, the
 * opposite of the clear-day gradient. That is a description of a colour rather
 * than a colour, so this is a value built to sit inside it: warm, hue 8°, and
 * measuring **0.027** relative luminance, within the recorded band.
 *
 * ⚠️ **Replace it with a sampled stop when one exists**, and do not treat this
 * number as measured in the meantime.
 */
const DUSK = '#4a221c';

/**
 * **Early twilight — the sun just below the horizon.** 📷 **From a photograph
 * haugur took at Urðarstígur on 2026-08-11**, minutes after saying the game's
 * June night was wrong.
 *
 * 🔴 **It is the measurement this file had been missing all day**, and it
 * corrects two things at once. The sky at Icelandic twilight is **LIGHT** —
 * nothing like `DUSK`'s luminance of 0.027 — and the warmth in it is **pink**,
 * not red-brown. Above the pink it is a pale grey-lilac, barely blue.
 *
 * ✅ **It also confirms the shape**: pale and cool above, warm below, in one
 * frame. That is the split this file had just been rebuilt around.
 *
 * ✅ **MEASURED, not eyeballed — haugur put the five files in
 * `Reference/03_Light_Weather/` the same evening**, so they were sampled by
 * §12.6's own method: median of a band, over the centre columns to keep
 * buildings out.
 *
 * | Frame | Facing | Warm band | |
 * |---|---|---|---|
 * | `16f635c4` | **toward the sun** | `#b99db1` | **R exceeds G by 28** |
 * | `dabd32b9` | **toward the sun** | `#c1a4b5` | **R exceeds G by 29** |
 * | `cffe2fa2` | away | `#9ab1da` | **R below B by 64** |
 * | `b0dd89c4` | away | `#b6c1dd` | **R below B by 39** |
 *
 * 🔴 **Same minute, opposite signs. That is V21 as a number rather than an
 * impression.**
 *
 * 🔴 **AND THEY ARE DARKENED TO ~62% OF WHAT THE PHOTOGRAPHS SHOW.** The measured
 * values came out **brighter than our overcast DAY sky** — a twilight zenith
 * summing 675 against daylight's 533 — which cannot be true, and a test caught
 * exactly that. **A phone auto-exposes**, so it
 * lifts a dim scene toward mid-grey; §12.6a already records the same trap for the
 * night frames (*"a night photograph is a long exposure and shows more than an
 * eye does"*). **The hue is the photographs'; the value is not.**
 */
const TWILIGHT_ZENITH_EARLY = '#667488';
const TWILIGHT_HORIZON_EARLY = '#7c8496';

/**
 * 🔴 **The sunset itself — the warmth that sits AROUND THE SUN.**
 *
 * 📷 **Measured from four photographs haugur took at Urðarstígur on 2026-08-11
 * at about 22:00**, when the sun was at **−0.7°** by our own solar model.
 *
 * ✅ **They were taken to answer one question and they answered it flatly.** Two
 * frames look north-west toward where the sun had gone: **vivid pink, orange low
 * down**. Two look away: **pale grey-lilac, almost no colour at all.** Same sky,
 * same minute.
 *
 * 🔴 **So a sunset is DIRECTIONAL, and treating it as a property of altitude was
 * the mistake underneath everything else today.** Mixing this much warmth evenly
 * round the horizon does not give a sunset — it gives grey, because pale blue and
 * salmon are near enough complementary to cancel. That is exactly what the
 * previous attempt produced.
 *
 * `TWILIGHT_*_EARLY` above are the **away-from-the-sun** sky from the same
 * frames; this is what gets added back toward the sun.
 *
 * ✅ **Measured at `#fd8689`** — the bright band low in `16f635c4`, three-quarters
 * of the way down the frame. 🔴 **It is SALMON-PINK, not orange.** The value
 * invented before the files arrived had blue at **93**; the real thing has
 * **137**. **That is the hue this would have got wrong**, and it is the whole
 * argument for `Reference/` in one number.
 *
 * ⚠️ **Scaled to 88%** rather than 62% like the base stops: this is *added* light
 * and the brightest thing in the frame, so auto-exposure lifted it least.
 */
const SUNSET_GLOW = '#df7679';

/**
 * **Late twilight at the zenith.** ✅ **Measured** — §12.6a, `nightsky9.webp`,
 * which that section explicitly sets aside as *"⚠️ Twilight, not night"*.
 *
 * 🔴 **It was excluded from the night stop for being twilight, and that is
 * exactly what makes it the right colour here.** Until 2026-08-11 the sky had no
 * twilight state at all: it went **day → dusk → night**, so `DUSK` — a warm
 * red-brown at luminance 0.027 — was the colour of the *entire dome* the moment
 * the sun touched the horizon.
 *
 * 🎯 **haugur, seeing a June midnight:** *"should not be all red… in iceland in
 * june its daylight almost 24 hours, but you might get a little red around the
 * horizion."*
 *
 * **He is describing two separate faults and both are fixed by this constant:**
 * the sky went dark far too early, and the warmth was everywhere instead of low
 * down. **A sunset colours the sky NEAR THE SUN.** The zenith now runs
 * day → this → night and never goes warm at all; only the horizon takes `DUSK`.
 */
const TWILIGHT_ZENITH = '#1f395a';

/**
 * The sun heights the sky is built around, in degrees.
 *
 * 🔴 **`CIVIL_END` is the number that fixes the Icelandic summer.** Civil
 * twilight — sun above −6° — is the band where it is still meaningfully light,
 * and at 64°N **the June sun never goes below about −3°**, so Iceland spends its
 * whole "night" inside it. **The sky must therefore still be bright at −3°**, and
 * the old ramp had it 94% of the way to a dark red-brown.
 *
 * ⚠️ **`NIGHT_FULL` is −15° rather than the −12° this used to use.** Nautical
 * twilight ends at −12 and astronomical at −18; **−15 is a deliberate middle**,
 * because a fixed monitor exposure cannot reproduce how far the eye adapts and
 * reaching full night at −12 arrived visibly too soon.
 */
const CIVIL_END = -6;
const NIGHT_FULL = -15;

/** Where early twilight has fully given way to late. */
const LATE_TWILIGHT = -11;

/**
 * Night. ✅ **Measured** — haugur supplied six night frames on 2026-08-11 and
 * this is sampled from them, replacing an invented value the same day.
 *
 * Taken from `nightsky2.jpg`, a moonlit clear night with stars: median zenith
 * **#1c2937, luminance 0.021, saturation 0.33, hue 211°**. Corroborated by
 * `nightsky5.webp` at **#282f46**, hue 226°.
 *
 * ✅ **The finding worth keeping: the night sky is the same HUE as the day sky.**
 * §12.6 found daylight settles at **195–219°** across every frame that has a hue
 * at all, and night lands at **211–226°**. It is the same blue, four stops down
 * — not a different colour.
 *
 * 🔴 **The invented value this replaced was `#0e141c` — about four times too
 * dark.** Guessing produced a night nobody had ever seen; one measurement moved
 * it a long way. That is the argument for `Reference/` in a single constant.
 *
 * ⚠️ **Two frames were deliberately NOT used.** `nightsky1.jpg` samples at hue
 * **169°, a green**, and `nightsky2.webp` at **264°, a purple** — both are
 * **norðurljós**, which is an event happening *in front of* the sky rather than
 * the sky itself. Averaging them in would have made every ordinary night faintly
 * green. **The aurora is its own feature — T114.**
 *
 * ⚠️ **One caveat on the method, since §12.6's daytime samples do not have it:**
 * a night photograph is a long exposure and shows more than an eye does. This
 * value is therefore likely to be a little brighter than a real night. It is
 * kept as measured for now because there is **no light source in the game** to
 * see a darker one by. ⚠️ **That caveat expired on 2026-08-11**: the moon is now
 * the light source and `DEFAULT_NIGHT_FLOOR` is 0 (**D60**). The stop is still the
 * measured one; the night around it is now genuinely dark.
 */
const NIGHT = '#1c2937';

/* ⚠️ Lower case throughout, and the mixer emits lower case too. One constant
   returned as typed while every blended value came back lower case would make
   an equality check on the colour pass or fail depending on the time of day. */

/**
 * The sun elevation haugur's light settings were tuned at, in degrees.
 *
 * 🔴 **This is what stops the clock silently undoing his work.** `DEFAULT_LIGHT`
 * is `sun 5.2 · ambient 1.9`, found by dragging sliders on 2026-08-07 with the
 * sun parked at **31°**. Those numbers mean *"the world looks right when the sun
 * is 31° up"*, so the daylight curve is normalised to be **exactly 1 there** and
 * his tuning survives.
 *
 * ⚠️ The consequence, which is correct and will still surprise: midsummer noon
 * reaches 49.4° here and is therefore **about 45% brighter** than what he tuned,
 * and midwinter noon reaches 2–3° and is a small fraction of it. **Iceland's
 * light really does do that.**
 */
const TUNED_ELEVATION = 31;

/** Below this the sun is set — the same −0.833° `sun.ts` uses. */
const HORIZON = -0.833;

export interface SkyLook {
  /**
   * The **horizon** stop — and the colour three separate things must take.
   *
   * 🔴 The fog, the `HemisphereLight`'s sky term and the bottom of the sky
   * gradient. Never the zenith; §12.6 says why.
   */
  readonly colour: string;
  /**
   * The **zenith** stop — the top of the sky gradient, and nothing else.
   *
   * ⚠️ Deliberately not the light and not the fog. It is the most saturated part
   * of the sky, and lighting the ground with it turns every shadow blue.
   */
  readonly zenith: string;
  /**
   * Multiplier on the tuned **sun** intensity. 0 once the sun has set.
   *
   * Shaped like `sin(elevation)`, because that genuinely is how much of a beam
   * of sunlight lands on a square metre of ground.
   */
  readonly sunFactor: number;
  /** Multiplier on the tuned **sky-light** intensity. Never quite 0 — see below. */
  readonly skyFactor: number;
  /**
   * How far you can see, as a multiplier on the clear-weather fog distances.
   *
   * 1 in ordinary weather; a small fraction in fog. ⚠️ **This is the one part of
   * the weather that still applies at night**, because fog is not a colour.
   */
  readonly viewFactor: number;
  /**
   * Multiplier for the **moon's own directional light** (T116, **D60**).
   *
   * 0 whenever the moon is down, new, or the sun is up. ⚠️ **A separate light
   * from the sun, not a share of it** — the moon is somewhere else in the sky,
   * and a moonlit shadow pointing the same way as the day's would be `sun.ts`'s
   * "north is −z" mistake all over again.
   */
  readonly moonFactor: number;
  /**
   * How strong the **sunset glow around the sun** is, 0…1 (2026-08-11).
   *
   * 🔴 **A separate output because it is DIRECTIONAL** — the two stops describe
   * the sky by altitude, and this one describes it by *where you are looking*.
   * The dome shader adds {@link SUNSET_GLOW} toward the sun, strongest low down,
   * scaled by this.
   *
   * Peaks with the sun just under the horizon and is gone by the end of civil
   * twilight, which is when haugur's photographs were taken.
   */
  readonly sunsetGlow: number;
  /** The colour of that glow — measured, and constant. */
  readonly glowColour: string;
}

/** What the sky needs to know about the moon. */
export interface MoonLight {
  /** Degrees above the horizon. Negative is below it, and gives no light. */
  readonly elevation: number;
  /** 0 new … 1 full. */
  readonly illumination: number;
}

/**
 * The weather, as the sky needs it: three numbers, all 0 to 1.
 *
 * ⚠️ **Not the wire type.** `Weather` carries eight fields in real units, and
 * three of them matter here. Converting at the edge keeps this function pure and
 * lets the client ease between two readings **as numbers** — a boolean fog flag
 * cannot be half-way, and a fog bank that arrives in one frame is exactly the
 * kind of snap piece ② went to trouble to avoid.
 */
export interface SkyWeather {
  /** 0 clear … 1 completely covered. */
  readonly cloud: number;
  /** 0 no fog … 1 sky obscured. */
  readonly fog: number;
  /** 0 dry … 1 heavy precipitation. */
  readonly rain: number;
}

/** Overcast, dry, no fog — what the sky is when nobody has said otherwise (V17). */
export const DEFAULT_SKY_WEATHER: SkyWeather = { cloud: 1, fog: 0, rain: 0 };

/**
 * How much darker a raining sky is than a merely overcast one.
 *
 * ⚠️ **Anchored on a measurement, but the anchor is a luminance and not a hex.**
 * §12.6's overcast group spans horizon luminance **0.237 to 0.599**, and the
 * chosen default `#a9b2ba` sits at **0.438**. The darkest frame in that group,
 * `sky2.jpg`, is **0.237** — about **0.54×** the default. Halving sRGB luminance
 * is roughly a **0.73×** move on the channels, so this is that, applied at full
 * rain.
 *
 * 🔴 **Rain is otherwise invisible.** Nothing falls — so if the sky does not
 * darken, the wettest country in the North Atlantic looks identical wet and dry.
 * This is the cheapest honest signal available, and it is a real one rather than
 * a stylisation.
 *
 * ⚠️ **This used to say "no particles, no sound", and half of that stopped being
 * true on 2026-08-31**: `smoke.ts` is a particle system now, built for the
 * hearth and the torch. 👉 **Rain is the next thing through it**, and when it
 * falls this constant is what it has to be reconciled with — a sky already
 * darkened by 0.73 plus visible rain may well be too much of both.
 */
const RAIN_DARKEN = 0.73;

/**
 * How much brighter the direct sun is on a clear day than on an overcast one.
 *
 * 🔴 **Overcast is 1, and that is what protects haugur's tuning.** He set
 * `sun 5.2 · ambient 1.9` by dragging sliders on 2026-08-07, and confirmed the
 * result on dev — *"daylight on dev works beautyfully."* Overcast is the default
 * state (V17), so the default state stays exactly what he approved and clear
 * weather is the departure from it.
 *
 * ⚠️ **Deliberately conservative, and it is a judgement rather than a
 * measurement.** Physically the gap is far larger — direct sun against a fully
 * diffuse sky. But this project has already shipped lighting that was blown out
 * (T84: *"surfaces clip toward white"*), and at midsummer noon the elevation
 * curve is already 1.45× on its own, so a generous number here multiplies into
 * 2× or more. **Raise it by eye if clear days look flat; do not compute it.**
 */
const CLEAR_SUN_GAIN = 0.25;

/**
 * How much of the ambient sky light a clear day gives up.
 *
 * The counterpart to `CLEAR_SUN_GAIN`, and the reason the two exist as a pair:
 * clear weather **moves** light from the diffuse term into the direct one rather
 * than adding it. Total illumination stays near what was tuned; what changes is
 * the contrast, which is what actually reads as sunshine — sharp shadows and a
 * dark side to everything.
 */
const CLEAR_SKY_LOSS = 0.3;

/** How much of the direct sun fog takes away. In real fog there is no sun at all. */
const FOG_SUN_LOSS = 0.85;

/**
 * How far you can see in fog, as a fraction of the ordinary view.
 *
 * ⚠️ **A judgement.** The observation says *sky obscured* and nothing about
 * visibility in metres. At the default render distance this puts the far plane at
 * roughly 200 m, which is a thick but walkable Icelandic fog — you can see the
 * ground and the next rise, and nothing beyond it.
 *
 * ⚠️ **It hides terrain that is still being drawn**, so fog is cheap on the GPU
 * and not expensive. It does **not** reduce what is loaded — streaming is
 * unchanged, and a fog bank must not make chunks unload.
 */
const FOG_VIEW = 0.12;

/** How much of the view heavy rain takes away. Far less than fog. */
const RAIN_VIEW_LOSS = 0.35;

/**
 * 🔴 **How dark the night is allowed to get — and it is now ZERO by default.**
 *
 * ⚠️ **This was `0.12`, a placeholder with a stated expiry**: *"when light
 * sources exist, bring it down"*. **The moon is that light source (D60)**, so
 * the expiry has arrived and the floor is gone.
 *
 * 🎯 **haugur, asked directly whether moonless nights should be genuinely
 * dark:** *"yes — dark means dark."* **D5** asks for the real winter dark, and a
 * full moon on a clear night is genuinely navigable while a moonless overcast one
 * is nearly black — which is what Iceland is actually like.
 *
 * ⚠️ **The cost he accepted with it:** on a dark night you can do very little
 * until there is fire (**T110**). **That is the point** — it turns the dark from
 * a usability problem into a reason to want light.
 *
 * ⚠️ **Still a parameter, and Dev Tools drives it**, because the right value is a
 * judgement made by standing in it rather than one computed here.
 */
export const DEFAULT_NIGHT_FLOOR = 0;

/**
 * How much of the tuned sky light a **full moon, overhead, in a clear sky**
 * gives.
 *
 * 🔴 **Wildly exaggerated, and it has to be.** A full moon delivers about
 * **0.25 lux** against overcast daylight's ~1,000 and direct sun's ~100,000 —
 * roughly **one part in four hundred thousand**. Rendered honestly the moon
 * would be a light you cannot see by, which is true and useless.
 *
 * ⚠️ **So this is a game number, not a measurement**, and it is the one to move
 * if moonlit nights feel wrong. It is set so a full moon is *workable* and a new
 * moon is *not* — the difference between the two being the whole point of D60.
 */
const MOON_FULL_LIGHT = 0.18;

/** How much of the moon's light heavy cloud takes away. */
const MOON_CLOUD_LOSS = 0.85;

/** Ease between two points, so nothing snaps at a boundary. */
function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

function mixHex(a: string, b: string, t: number): string {
  const channel = (hex: string, at: number): number => parseInt(hex.slice(at, at + 2), 16);
  const out = [1, 3, 5].map((at) => {
    const value = Math.round(channel(a, at) + (channel(b, at) - channel(a, at)) * t);
    return Math.min(255, Math.max(0, value)).toString(16).padStart(2, '0');
  });
  return `#${out.join('')}`;
}

/** Scale every channel of a colour, for the rain darkening. */
function scaleHex(hex: string, factor: number): string {
  const out = [1, 3, 5].map((at) => {
    const value = Math.round(parseInt(hex.slice(at, at + 2), 16) * factor);
    return Math.min(255, Math.max(0, value)).toString(16).padStart(2, '0');
  });
  return `#${out.join('')}`;
}

/** Keep a weather number inside 0…1, whatever arrives. */
const unit = (v: number): number => (Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0);

/**
 * The two daylight stops for this weather, before the time of day is applied.
 *
 * Clear → overcast → **darkened by rain** → **fog last**, because fog replaces
 * the sky rather than tinting it: `sky 5 foggy.jpg` has no gradient and no hue
 * to speak of, and a fog that still carried a blue zenith would read as haze.
 */
function daylightStops(weather: SkyWeather): { horizon: string; zenith: string } {
  const cloud = unit(weather.cloud);
  const rain = unit(weather.rain);
  const fog = unit(weather.fog);

  let horizon = mixHex(CLEAR, DAY, cloud);
  let zenith = mixHex(CLEAR_ZENITH, DAY_ZENITH, cloud);

  if (rain > 0) {
    horizon = mixHex(horizon, scaleHex(horizon, RAIN_DARKEN), rain);
    zenith = mixHex(zenith, scaleHex(zenith, RAIN_DARKEN), rain);
  }

  if (fog > 0) {
    horizon = mixHex(horizon, FOG, fog);
    zenith = mixHex(zenith, FOG, fog);
  }

  return { horizon, zenith };
}

/**
 * What the sky should look like with the sun here and the weather doing this.
 *
 * @param elevation degrees above the horizon, from `sunPosition`.
 * @param weather   0–1 cloud, fog and rain. Omitted means **overcast** (V17) —
 *                  and that default is why every existing test still passes.
 */
export function skyLook(
  elevation: number,
  weather: SkyWeather = DEFAULT_SKY_WEATHER,
  moon: MoonLight | null = null,
  nightFloor: number = DEFAULT_NIGHT_FLOOR,
): SkyLook {
  const day = daylightStops(weather);

  /* 🔴 **The two stops now travel DIFFERENT paths, and that is the whole point.**
     A sunset colours the sky near the sun, not overhead — so the zenith runs
     day → twilight → night and never goes warm, while the horizon runs
     day → dusk → night and does.

     ⚠️ **Until 2026-08-11 both took the warm path**, so a June midnight in
     Iceland — sun about −3° — came out as a dark red-brown cave. §12.6 had
     recorded the shape of the answer all along: dusk is *"brighter and more
     saturated toward the horizon"*, the opposite of a clear day. */
  /* 🔴 **Biased hard toward the light, and this is the Icelandic summer.**
     A plain `smoothstep` across civil twilight puts the sun's −3° — where the
     June night bottoms out at 64°N — at **half way to dusk**, which came out as
     a dark red-brown cave. 🎯 haugur: *"in iceland in june its daylight almost 24
     hours, but you might get a little red around the horizion."*
     The square root holds it at **71% daylight** there instead.

     ⚠️ **This is compression, not physics.** Real sky brightness falls about a
     hundredfold from sunset to the end of civil twilight; a monitor at fixed
     exposure cannot show that, and the eye that would have adapted is not in the
     room. **The curve is bent to match what the sky LOOKS like, and haugur is
     the instrument.** */
  const toDay = Math.sqrt(smoothstep(CIVIL_END, 0, elevation));
  const toLate = smoothstep(LATE_TWILIGHT, CIVIL_END, elevation);
  const toNight = smoothstep(NIGHT_FULL, LATE_TWILIGHT, elevation);

  /**
   * Three bands, each between two measured stops.
   *
   * 🔴 **Early twilight is from haugur's own photograph of an August evening**,
   * late twilight from `nightsky9.webp` and `dusk.jpg`. Before 2026-08-11 there
   * was **one** twilight — the late one — so the sky reached a dark red-brown the
   * moment the sun set, across the whole dome.
   */
  const ramp = (dayStop: string, early: string, late: string): string =>
    elevation >= 0
      ? /* Above the horizon the day owns it; the change is all below. */
        dayStop
      : elevation >= CIVIL_END
        ? /* Civil twilight — still light. Iceland's whole summer night is here. */
          mixHex(early, dayStop, toDay)
        : elevation >= LATE_TWILIGHT
          ? mixHex(late, early, toLate)
          : mixHex(NIGHT, late, toNight);

  const colour = ramp(day.horizon, TWILIGHT_HORIZON_EARLY, DUSK);
  const zenith = ramp(day.zenith, TWILIGHT_ZENITH_EARLY, TWILIGHT_ZENITH);

  /* How much of the weather's effect on LIGHT applies. Fades out with the sun:
     the difference between a clear day and an overcast one is the direct beam,
     and after dark there is no beam to make a difference to. */
  /* ⚠️ Widened on 2026-08-11 to match the colour. The light used to reach full
     night by −12°, which put an Icelandic June midnight — sun about −3° — at
     roughly half light. It is nearly full daylight there, and the sky says so. */
  const daylight = Math.sqrt(smoothstep(NIGHT_FULL, 3, elevation));
  const clear = (1 - unit(weather.cloud)) * daylight;
  const fogged = unit(weather.fog) * daylight;

  /* Sun: physically shaped, normalised to haugur's tuning, and off once set.
     The last few degrees are eased so the light does not cut out at the moment
     the disc touches the horizon — which is not what sunset looks like. */
  const above = Math.max(0, Math.sin((elevation * Math.PI) / 180));
  const sunFactor =
    elevation <= HORIZON
      ? 0
      : (above / Math.sin((TUNED_ELEVATION * Math.PI) / 180)) *
        smoothstep(HORIZON, 3, elevation) *
        (1 + CLEAR_SUN_GAIN * clear) *
        (1 - FOG_SUN_LOSS * fogged);

  /* 🌕 The moon (D60). Physically shaped like the sun — `sin(elevation)` is how
     much of a beam lands on a square metre — times how much of the disc is lit,
     times what the cloud leaves. ⚠️ Faded out by daylight: a moon in a blue sky
     is a lovely thing to look at and lights nothing. */
  const moonUp = moon === null ? 0 : Math.max(0, Math.sin((moon.elevation * Math.PI) / 180));
  const moonFactor =
    moon === null
      ? 0
      : MOON_FULL_LIGHT *
        moonUp *
        unit(moon.illumination) *
        (1 - MOON_CLOUD_LOSS * unit(weather.cloud)) *
        (1 - daylight);

  /* Sky light: full by 6° up, down to the floor by the end of twilight, given up
     on a clear day in exchange for the sun above — and lifted a little by the
     moon, so a moonlit night is not lit from one side only. */
  const skyFactor =
    (nightFloor + (1 - nightFloor) * Math.sqrt(smoothstep(NIGHT_FULL, 3, elevation))) *
      (1 - CLEAR_SKY_LOSS * clear) +
    moonFactor * 0.5;

  /* ⚠️ Distance, unlike colour, applies at night too. Fog at midnight is still
     fog, and the one thing a player can tell about it in the dark is that the
     world ends closer than it should. */
  const viewFactor =
    (1 - (1 - FOG_VIEW) * unit(weather.fog)) * (1 - RAIN_VIEW_LOSS * unit(weather.rain));

  /* 🌇 The glow around the sun. A bell centred just under the horizon: nothing
     while the sun is well up, strongest as it sets, gone by the end of civil
     twilight. ⚠️ **Cloud kills it** — an overcast sunset is grey, and haugur's
     photographs are of a sky with high cloud catching the light, which is a
     thing we cannot draw yet because we have no clouds. */
  const glowBand = Math.exp(-(((elevation + 1.5) / 4.5) ** 2));
  const sunsetGlow = glowBand * (1 - unit(weather.cloud) * 0.8) * (1 - unit(weather.fog));

  return {
    colour,
    zenith,
    sunFactor,
    skyFactor,
    viewFactor,
    moonFactor,
    sunsetGlow,
    glowColour: SUNSET_GLOW,
  };
}
